import { type Theme } from '@mui/material/styles';
import { type SystemStyleObject } from '@mui/system';

import { INK_DARK, INK_LIGHT } from './ink';
import { BEAM_ORIGIN_SX } from './masks';
import { recipes } from './recipes';
import { patternScaleSx } from './scale';
import { type Paint, type PatternSx, type SectionPatternName } from './types';

/**
 * Section background patterns — the second half of the backdrop dialect the
 * hero already speaks (HeroStage's two-tier masked grid and its radial mesh).
 *
 * The set is split by the question each file answers, because they are
 * genuinely independent axes and were tangled before:
 *
 * - `recipes.ts` — what a motif is made of, and which band it is for.
 * - `scale.ts`   — how dense it is in the box it is actually painted into.
 * - `ink.ts`     — how dark a mark is, per colour scheme.
 * - `masks.ts`   — where it stops.
 * - `geometry.ts`— how a 1px rule is drawn so it survives a real screen.
 *
 * This file only composes them. Nothing in the set reads the viewport, holds
 * state or runs on the client; a pattern is paint, and a band wearing one
 * costs two `<div>`s and no JavaScript.
 */
export { patternContainerSx } from './scale';
export { type SectionPatternName } from './types';

/**
 * `mask-image` still needs the prefixed property for Safari, and the two
 * always carry the same value, so no recipe states it twice.
 */
const withMask = ({ maskImage, glow, ...paint }: Paint) => ({
  ...paint,
  ...(maskImage && { maskImage, WebkitMaskImage: maskImage }),
  ...(glow && {
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: glow,
      pointerEvents: 'none',
    },
  }),
});

/**
 * The pattern's paint, as `sx` entries.
 *
 * Returned as an array rather than one object for the same reason
 * `glassSurfaceSx` is: the dark-scheme ink has to come from
 * `theme.applyStyles`, which knows the selector the theme actually emits, and
 * nesting its `CSSObject` inside a style literal would give that literal an
 * index signature and stop every other property in it type-checking as
 * itself. Spread it, don't nest it.
 *
 * `aurora` is scheme-independent and has no period, so it is handed back as
 * paint alone — two fewer rules in the stylesheet for the band that needs
 * neither.
 */
export const sectionPatternSx = (name: SectionPatternName): PatternSx[] => {
  const paint = withMask(recipes[name]);

  if (name === 'aurora') return [paint];

  // `beams` is aimed at the form rather than at the band, so it carries the
  // origin its rays, its mask and its bloom all read from.
  const aim: PatternSx[] = name === 'beams' ? [BEAM_ORIGIN_SX] : [];

  return [
    patternScaleSx,
    ...aim,
    { ...INK_LIGHT, ...paint },
    (theme) => theme.applyStyles('dark', INK_DARK) as SystemStyleObject<Theme>,
  ];
};
